import moment from 'moment'
import { ServicingTypes } from 'app/models/servicing-params'
import { stringToDate } from 'app/utils/util-functions'

/** Look-ahead the legacy builders used when no range is picked. */
export const MYWORK_DEFAULT_HORIZON_DAYS = 10

/** Row types the My Work Unscheduled tab lists. */
export const MYWORK_UNSCHEDULED_TYPES: readonly ServicingTypes[] = [
  ServicingTypes.newBorrowing,
  ServicingTypes.principalPayment,
  ServicingTypes.pik,
]

/** Row types the My Work Scheduled tab lists (rollover splits were never listed there). */
export const MYWORK_SCHEDULED_TYPES: readonly ServicingTypes[] = [
  ServicingTypes.loanRollover,
  ServicingTypes.loanIncrease,
  ServicingTypes.maturingLoan,
  ServicingTypes.loanRateSetting,
  ServicingTypes.loanAddRates,
  ServicingTypes.amortizationSchedule,
  ServicingTypes.feePayment,
  ServicingTypes.loanConversion,
  ServicingTypes.loanMerge,
  ServicingTypes.maturingDeal,
  ServicingTypes.maturingFacility,
  ServicingTypes.fee,
  ServicingTypes.pikScheduleLoan,
  ServicingTypes.facilityReverse,
]

const isCompleteRange = (
  dateRange?: [Date, Date] | [Date | null, Date | null],
): dateRange is [Date, Date] =>
  Array.isArray(dateRange) &&
  dateRange.length === 2 &&
  dateRange[0] instanceof Date &&
  dateRange[1] instanceof Date

/**
 * Upper bound (`to`) to request from GET /api/servicing/events for My Work.
 *
 * Reproduces the legacy builders exactly: they anchored on the range end (or
 * the business date) and looked ahead by the range length (or 10 days), i.e.
 * `to = anchor + width`. Rows before the anchor are still returned by the feed
 * and trimmed by `filterMyWorkRows`, as before.
 */
export function myWorkFeedUpperBound(
  date: Date,
  useDateRange?: boolean,
  dateRange?: [Date, Date] | [Date | null, Date | null],
): string {
  if (useDateRange && isCompleteRange(dateRange)) {
    const widthDays = moment(dateRange[1]).diff(moment(dateRange[0]), 'days')
    return moment(dateRange[1]).add(widthDays, 'days').format('YYYY-MM-DD')
  }
  return moment(date)
    .add(MYWORK_DEFAULT_HORIZON_DAYS, 'days')
    .format('YYYY-MM-DD')
}

const toLocalDate = (value: string | Date): Date =>
  value instanceof Date ? value : stringToDate(value)

/**
 * Same post-filter as the legacy My Work pages: rows inside the picked range,
 * or, without a range, rows dated on the business date.
 */
export function filterMyWorkRows<T extends { effectiveDate: string | Date }>(
  rows: T[],
  date: Date,
  useDateRange?: boolean,
  dateRange?: [Date, Date] | [Date | null, Date | null],
): T[] {
  return rows.filter(row => {
    const effectiveDate = toLocalDate(row.effectiveDate)
    if (useDateRange && isCompleteRange(dateRange)) {
      const start = new Date(dateRange[0])
      const end = new Date(dateRange[1])
      return effectiveDate >= start && effectiveDate <= end
    }
    return (
      date.getFullYear() === effectiveDate.getFullYear() &&
      date.getMonth() === effectiveDate.getMonth() &&
      date.getDate() === effectiveDate.getDate()
    )
  })
}
